// =============================================================================
// Utility Functions
// =============================================================================

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, isToday, isYesterday, isTomorrow } from 'date-fns';
import { getDateLocale } from '../hooks/useDateLocale';
import i18n from '../i18n';

// =============================================================================
// Classname Utility
// =============================================================================

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// =============================================================================
// Date Formatting
// =============================================================================
// ATTENZIONE: questi helper formattano nel fuso del BROWSER. Vanno bene solo
// per istanti che riguardano chi sta guardando (es. "2 ore fa"), MAI per orari
// di appuntamenti, disponibilità o report: quelli appartengono al calendario
// dell'organizzazione e vanno resi con le funzioni di lib/tenantTime.ts,
// passando il fuso di useTenantTimeZone().

/**
 * Formatta una data in formato italiano
 */
export function formatDate(date: Date | string, formatStr: string = 'dd/MM/yyyy'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, formatStr, { locale: getDateLocale(i18n.language) });
}

/**
 * Formatta una data con orario
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'dd/MM/yyyy HH:mm', { locale: getDateLocale(i18n.language) });
}

/**
 * Formatta solo l'orario
 */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return format(d, 'HH:mm', { locale: getDateLocale(i18n.language) });
}

/**
 * Formatta una data in modo "smart" (Oggi, Ieri, ecc.)
 */
export function formatSmartDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isToday(d)) return `Oggi, ${format(d, 'HH:mm')}`;
  if (isYesterday(d)) return `Ieri, ${format(d, 'HH:mm')}`;
  if (isTomorrow(d)) return `Domani, ${format(d, 'HH:mm')}`;
  
  return format(d, 'dd MMM yyyy, HH:mm', { locale: getDateLocale(i18n.language) });
}

/**
 * Formatta durata relativa (es: "2 ore fa")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: getDateLocale(i18n.language) });
}

// =============================================================================
// Currency Formatting
// =============================================================================

/**
 * Formatta un numero come valuta EUR
 */
export function formatCurrency(
  amount: number,
  currency: string = 'EUR',
  locale: string = 'it-IT'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Formatta minuti in formato ore:minuti leggibile
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}

// =============================================================================
// String Utilities
// =============================================================================

/**
 * Tronca una stringa con ellipsis
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length).trim() + '...';
}

/**
 * Genera le iniziali da un nome
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Genera uno slug URL-friendly da una stringa
 */
export function generateSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/**
 * Capitalizza la prima lettera
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// =============================================================================
// Error Handling
// =============================================================================

/**
 * Estrae il messaggio di errore da vari tipi di errore
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'object' && error !== null) {
    // Axios error
    const axiosError = error as any;
    if (axiosError.response?.data?.message) {
      const msg = axiosError.response.data.message;
      return Array.isArray(msg) ? msg.join(', ') : msg;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  return 'Si è verificato un errore imprevisto';
}

/**
 * Check se l'errore è un errore di rete
 */
export function isNetworkError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;
    return !axiosError.response && !!axiosError.request;
  }
  return false;
}

// =============================================================================
// Validation Utilities
// =============================================================================

/**
 * Valida un'email
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Valida un numero di telefono italiano
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^(\+39)?[\s]?[0-9]{10}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
}

// =============================================================================
// Color Utilities
// =============================================================================

/**
 * Genera un colore di contrasto (bianco o nero) per un colore di sfondo
 */
export function getContrastColor(hexColor: string): 'white' | 'black' {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Formula luminosità
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? 'black' : 'white';
}

/**
 * Schiarisce un colore hex
 */
export function lightenColor(hexColor: string, percent: number): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}
